import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Search,
  RefreshCw,
  Video,
  Clock,
  CheckCircle2,
  XCircle,
  Play,
  Eye,
  FileText,
  Loader2,
  Calendar,
  Download,
  AlertTriangle,
  Brain,
  Activity,
  Shield,
  Target,
  Sparkles,
  Star,
  MessageSquare,
  User,
  Award,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ScanFace,
  Waves,
  MonitorX,
  HeartPulse,
  EyeIcon,
  Fingerprint,
  FolderArchive
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Proctoring Score Card Component
function ProctoringCard({ icon: Icon, title, value, unit, status, description }) {
  const getStatusColor = () => {
    switch (status) {
      case 'good': return 'from-green-500/20 to-green-600/10 border-green-500/30';
      case 'warning': return 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30';
      case 'critical': return 'from-red-500/20 to-red-600/10 border-red-500/30';
      default: return 'from-slate-500/20 to-slate-600/10 border-slate-500/30';
    }
  };

  const getTextColor = () => {
    switch (status) {
      case 'good': return 'text-green-600 dark:text-green-400';
      case 'warning': return 'text-yellow-600 dark:text-yellow-400';
      case 'critical': return 'text-red-600 dark:text-red-400';
      default: return 'text-muted-foreground';
    }
  };

  const getIconBg = () => {
    switch (status) {
      case 'good': return 'bg-green-500/20';
      case 'warning': return 'bg-yellow-500/20';
      case 'critical': return 'bg-red-500/20';
      default: return 'bg-muted';
    }
  };

  return (
    <div className={`p-4 rounded-xl bg-gradient-to-br ${getStatusColor()} border`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${getIconBg()}`}>
          <Icon className={`w-5 h-5 ${getTextColor()}`} />
        </div>
        {status === 'critical' && (
          <AlertCircle className="w-4 h-4 text-red-500 animate-pulse" />
        )}
      </div>
      <p className="text-muted-foreground text-xs uppercase tracking-wider mb-1">{title}</p>
      <div className="flex items-baseline gap-1">
        <span className={`text-2xl font-bold ${getTextColor()}`}>{value}</span>
        {unit && <span className="text-muted-foreground text-sm">{unit}</span>}
      </div>
      {description && (
        <p className="text-muted-foreground/70 text-xs mt-2">{description}</p>
      )}
    </div>
  );
}

// Integrity Score Ring Component
function IntegrityScoreRing({ score }) {
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  const getColor = () => {
    if (score >= 80) return '#22c55e';
    if (score >= 60) return '#eab308';
    return '#ef4444';
  };

  return (
    <div className="relative w-32 h-32">
      <svg className="w-32 h-32 transform -rotate-90">
        <circle cx="64" cy="64" r="45" className="stroke-muted" strokeWidth="8" fill="none" />
        <circle cx="64" cy="64" r="45" stroke={getColor()} strokeWidth="8" fill="none" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} className="transition-all duration-1000" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-foreground">{score}</span>
        <span className="text-xs text-muted-foreground">Integrity</span>
      </div>
    </div>
  );
}

// Video Player Modal Component
function VideoPlayerModal({ isOpen, onClose, videoUrl, title }) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef(null);
  
  if (!isOpen || !videoUrl) return null;
  
  const fileId = videoUrl.match(/\/d\/([^/]+)/)?.[1];
  const streamUrl = fileId ? `https://drive.google.com/uc?export=download&id=${fileId}` : videoUrl;
  const downloadUrl = streamUrl;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-blue-500" />
            {title || 'Video Recording'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
            {isLoading && !hasError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 z-10">
                <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                <p className="text-white font-medium">Loading Video...</p>
              </div>
            )}
            
            {hasError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 z-10">
                <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
                <p className="text-white font-medium">Video Processing</p>
                <p className="text-slate-400 text-sm mt-1 text-center px-4">
                  The video is still being processed. Please download to view.
                </p>
                <Button className="mt-4 bg-green-500 hover:bg-green-600 text-white" onClick={() => window.open(downloadUrl, '_blank')}>
                  <Download className="w-4 h-4 mr-2" />
                  Download Video
                </Button>
              </div>
            )}
            
            <video ref={videoRef} src={streamUrl} controls className="absolute inset-0 w-full h-full" onLoadedData={() => { setIsLoading(false); setHasError(false); }} onError={() => { setIsLoading(false); setHasError(true); }} preload="metadata">
              Your browser does not support video playback.
            </video>
          </div>
          
          <div className="flex justify-center gap-3 flex-wrap">
            <Button variant="outline" onClick={() => window.open(downloadUrl, '_blank')} className="border-green-500 text-green-600 dark:text-green-400">
              <Download className="w-4 h-4 mr-2" />
              Download Video
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AgentInterviews() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [interviews, setInterviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all'); // 'all', 'self', 'partner'
  
  // Detail modal state
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Video modal state
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [currentVideoTitle, setCurrentVideoTitle] = useState('');
  const [downloadingAll, setDownloadingAll] = useState(false);

  useEffect(() => {
    if (token) {
      fetchInterviews();
    }
  }, [token]);

  const fetchInterviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/agent/interviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInterviews(response.data);
    } catch (error) {
      console.error('Error fetching interviews:', error);
      toast.error('Failed to load interviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchInterviewDetails = async (interviewId) => {
    try {
      setLoadingDetails(true);
      const response = await axios.get(`${API}/api/agent/interview/${interviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedInterview(response.data);
      setDetailsOpen(true);
      setActiveTab('overview');
    } catch (error) {
      console.error('Error fetching interview details:', error);
      toast.error('Failed to load interview details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const openVideoModal = (url, title) => {
    setCurrentVideoUrl(url);
    setCurrentVideoTitle(title);
    setVideoModalOpen(true);
  };

  const downloadAllVideos = async (interviewId, studentName) => {
    setDownloadingAll(true);
    try {
      toast.info('Preparing download links...');
      
      const response = await axios.get(
        `${API}/api/agent/interview/${interviewId}/download-all-videos`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { video_files } = response.data;
      if (!video_files || video_files.length === 0) {
        toast.error('No video recordings found for this interview');
        return;
      }
      
      // Open download links
      video_files.forEach((file, idx) => {
        setTimeout(() => {
          window.open(file.download_url, '_blank');
        }, idx * 500);
      });
      
      toast.success(`Opening ${video_files.length} video download(s)...`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download videos. Please try again.');
    } finally {
      setDownloadingAll(false);
    }
  };

  const getStatusBadge = (status, interview) => {
    // For agents: show "Completed" even if interview is under_review (they don't need to know about internal review process)
    // The result column will show "Processing" until admin approves
    if ((status === 'under_review' || status === 'completed') && !interview?.final_verdict) {
      return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" /> Completed</Badge>;
    }
    
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-600 border-yellow-200"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'in_progress':
        return <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200"><Play className="w-3 h-3 mr-1" /> In Progress</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" /> Completed</Badge>;
      case 'under_review':
        return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" /> Completed</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200"><XCircle className="w-3 h-3 mr-1" /> Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const filteredInterviews = interviews.filter(interview => {
    const matchesSearch = 
      interview.interview_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      interview.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      interview.student_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      interview.university_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      interview.agent_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || interview.status === statusFilter;
    
    // Filter by agent type (self vs partner)
    const matchesAgentFilter = agentFilter === 'all' || 
      (agentFilter === 'self' && !interview.is_partner_interview) ||
      (agentFilter === 'partner' && interview.is_partner_interview);
    
    return matchesSearch && matchesStatus && matchesAgentFilter;
  });

  const stats = {
    total: interviews.length,
    pending: interviews.filter(i => i.status === 'pending').length,
    completed: interviews.filter(i => i.status === 'completed').length,
    inProgress: interviews.filter(i => i.status === 'in_progress').length,
    underReview: interviews.filter(i => i.status === 'under_review').length,
    selfInterviews: interviews.filter(i => !i.is_partner_interview).length,
    partnerInterviews: interviews.filter(i => i.is_partner_interview).length
  };

  // Calculate proctoring metrics from selected interview
  const getProctoringMetrics = () => {
    if (!selectedInterview) return null;
    const proctoring = selectedInterview.proctoring_summary || {};
    
    // Handle both snake_case variants (eye_contact_percent vs eye_contact_percentage)
    const eyeContactPercent = proctoring.eye_contact_percent || proctoring.eye_contact_percentage || proctoring.eyeContactPercent || 0;
    const tabSwitches = proctoring.tab_switch_count || proctoring.tab_switches || proctoring.tabSwitchCount || 0;
    const facesDetected = proctoring.multiple_faces_count || proctoring.multiple_faces_detected || proctoring.multipleFacesCount || 0;
    const noFaceCount = proctoring.face_absent_count || proctoring.no_face_detected_count || proctoring.faceAbsentCount || 0;
    const avgConfidence = proctoring.average_confidence || proctoring.face_match_percent / 100 || proctoring.faceMatchPercent / 100 || 0;
    const faceMatchPercent = proctoring.face_match_percent || proctoring.face_match_percentage || proctoring.faceMatchPercent || 0;
    const livenessScore = proctoring.liveness_score || proctoring.livenessScore || 0;
    const blinkCount = proctoring.blink_count || proctoring.blinkCount || 0;
    
    // Calculate integrity score
    let integrityScore = 100;
    integrityScore -= Math.min(20, tabSwitches * 5);
    integrityScore -= Math.min(30, facesDetected * 10);
    integrityScore -= Math.min(20, noFaceCount * 2);
    integrityScore -= Math.max(0, 50 - eyeContactPercent) * 0.4;
    integrityScore = Math.max(0, Math.round(integrityScore));
    
    return { 
      eyeContactPercent, 
      tabSwitches, 
      facesDetected, 
      noFaceCount, 
      avgConfidence: avgConfidence || faceMatchPercent / 100,
      integrityScore,
      faceMatchPercent,
      livenessScore,
      blinkCount
    };
  };

  return (
    <DashboardLayout role="agent">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">Interviews</h1>
            <p className="text-muted-foreground">View all interviews and detailed reports</p>
          </div>
          <Button variant="outline" onClick={fetchInterviews}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('all')}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <Video className="w-8 h-8 text-lime-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('pending')}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('in_progress')}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
                </div>
                <Play className="w-8 h-8 text-blue-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('completed')}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search & Filter */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex gap-4 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search by student, email, university, or agent..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-interviews" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="under_review">Under Review ({stats.underReview})</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={agentFilter} onValueChange={setAgentFilter}>
                <SelectTrigger className="w-48" data-testid="agent-filter">
                  <SelectValue placeholder="Agent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agents ({stats.total})</SelectItem>
                  <SelectItem value="self">My Interviews ({stats.selfInterviews})</SelectItem>
                  <SelectItem value="partner">Partner Interviews ({stats.partnerInterviews})</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Interviews Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-lime-500" />
              Interview List
            </CardTitle>
            <CardDescription>
              {filteredInterviews.length} interview{filteredInterviews.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
              </div>
            ) : filteredInterviews.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Video className="w-12 h-12 mx-auto mb-4 opacity-20" />
                <p>No interviews found</p>
                <p className="text-sm mt-1">Create an application to schedule interviews</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>University</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Result</TableHead>
                    <TableHead>AI Score</TableHead>
                    <TableHead>Integrity</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInterviews.map((interview) => {
                    // Only show scores if admin has reviewed (final_verdict is set)
                    const isAdminReviewed = !!interview.final_verdict;
                    const aiScore = isAdminReviewed ? interview.ai_analysis?.overall_score : null;
                    const integrityScore = isAdminReviewed ? (interview.proctoring_summary?.integrity_score || 
                      interview.ai_analysis?.integrity_score) : null;
                    // Result is only shown after admin review
                    const result = interview.final_verdict;
                    // Check if interview has been completed/submitted but not yet reviewed by admin
                    // Block results for: completed, under_review, or any status that has answers but no final verdict
                    const hasAnswers = interview.answers?.length > 0 || interview.questions_answers?.length > 0;
                    const isPendingReview = !isAdminReviewed && (interview.status === 'completed' || interview.status === 'under_review' || hasAnswers);
                    // Only allow viewing for pending (not started) interviews
                    const canViewDetails = isAdminReviewed || interview.status === 'pending';
                    
                    return (
                      <TableRow key={interview.id} data-testid={`interview-row-${interview.id}`}>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">
                            {interview.interview_number || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{interview.student_name}</p>
                            <p className="text-sm text-muted-foreground">{interview.student_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{interview.university_name || 'N/A'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span className={`text-sm ${interview.is_partner_interview ? 'text-blue-600 font-medium' : 'text-muted-foreground'}`}>
                              {interview.agent_name || 'Self'}
                            </span>
                            {interview.is_partner_interview && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                                Partner
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(interview.status, interview)}</TableCell>
                        <TableCell>
                          {/* Result - only show after admin has reviewed */}
                          {isPendingReview ? (
                            <Badge variant="outline" className="bg-orange-50 text-orange-600 border-orange-200">
                              Processing (takes up to 8 hours)
                            </Badge>
                          ) : result ? (
                            <Badge className={result === 'pass' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-red-100 text-red-700 border-red-300'}>
                              {result.toUpperCase()}
                            </Badge>
                          ) : interview.status === 'pending' ? (
                            <Badge variant="outline" className="text-muted-foreground">-</Badge>
                          ) : interview.status === 'in_progress' ? (
                            <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">In Progress</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-orange-50 text-orange-600 border-orange-200">
                              Processing (takes up to 8 hours)
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* Only show AI score after admin review */}
                          {isAdminReviewed && aiScore !== undefined ? (
                            <span className={`font-bold px-2 py-1 rounded ${getScoreColor(aiScore)}`}>
                              {aiScore}%
                            </span>
                          ) : isPendingReview || interview.status === 'under_review' ? (
                            <span className="text-orange-500 text-sm">Pending</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* Only show integrity score after admin review */}
                          {isAdminReviewed && integrityScore !== undefined ? (
                            <span className={`font-bold px-2 py-1 rounded ${getScoreColor(integrityScore)}`}>
                              {integrityScore}%
                            </span>
                          ) : isPendingReview || interview.status === 'under_review' ? (
                            <span className="text-orange-500 text-sm">Pending</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(interview.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {/* Show different actions based on review status */}
                            {isAdminReviewed ? (
                              <>
                                <Button 
                                  variant="default" 
                                  size="sm"
                                  onClick={() => fetchInterviewDetails(interview.id)}
                                  disabled={loadingDetails}
                                  className="bg-lime-600 hover:bg-lime-700 text-white"
                                  title="View Detailed Report"
                                >
                                  {loadingDetails ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                                  Detailed Report
                                </Button>
                                {interview.full_recording_url && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => openVideoModal(interview.full_recording_url, `${interview.student_name} - Full Recording`)}
                                    title="Watch Recording"
                                  >
                                    <Play className="w-4 h-4" />
                                  </Button>
                                )}
                              </>
                            ) : interview.status === 'pending' ? (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => fetchInterviewDetails(interview.id)}
                                disabled={loadingDetails}
                                title="View Details"
                              >
                                {loadingDetails ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                              </Button>
                            ) : (
                              <span className="text-xs text-orange-500 px-2">Results pending admin review</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Video Modal */}
      <VideoPlayerModal isOpen={videoModalOpen} onClose={() => setVideoModalOpen(false)} videoUrl={currentVideoUrl} title={currentVideoTitle} />

      {/* Interview Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-lime-500" />
              Interview Report: {selectedInterview?.student_name}
            </DialogTitle>
            <DialogDescription>
              {selectedInterview?.university_name} • {selectedInterview?.created_at && new Date(selectedInterview.created_at).toLocaleDateString()}
            </DialogDescription>
          </DialogHeader>

          {selectedInterview && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              {/* Check if results are available - block for ANY interview that has started but not been reviewed */}
              {(() => {
                const hasAnswers = selectedInterview.answers?.length > 0 || selectedInterview.questions_answers?.length > 0;
                const isReviewed = !!selectedInterview.final_verdict;
                const isPending = selectedInterview.status === 'pending';
                // Block if: has answers OR completed/under_review AND not yet reviewed by admin
                const shouldBlockResults = !isReviewed && (hasAnswers || selectedInterview.status === 'completed' || selectedInterview.status === 'under_review' || selectedInterview.status === 'in_progress');
                
                if (shouldBlockResults && !isPending) {
                  return (
                    <div className="flex flex-col items-center justify-center py-12 space-y-4">
                      <div className="w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                      </div>
                      <h3 className="text-xl font-semibold">Results Under Review</h3>
                      <p className="text-muted-foreground text-center max-w-md">
                        This interview is currently being reviewed by the administration team. 
                        Results and analytics will be available once the review is complete.
                      </p>
                      <Badge variant="outline" className="bg-orange-50 text-orange-600 border-orange-200">
                        Status: Awaiting Admin Approval
                      </Badge>
                    </div>
                  );
                }
                
                return (
              <>
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="answers">Q&A Responses</TabsTrigger>
                <TabsTrigger value="proctoring">Proctoring</TabsTrigger>
                <TabsTrigger value="ai">AI Analysis</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Student Info */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Student Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div>
                        <p className="text-xs text-muted-foreground">Name</p>
                        <p className="font-medium">{selectedInterview.student_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="text-sm">{selectedInterview.student_email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">University</p>
                        <p className="text-sm">{selectedInterview.university_name}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* AI Score */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Brain className="w-4 h-4" />
                        AI Assessment Score
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex items-center justify-center">
                      {selectedInterview.ai_analysis?.overall_score !== undefined ? (
                        <div className="text-center">
                          <div className={`text-5xl font-bold ${getScoreColor(selectedInterview.ai_analysis.overall_score).split(' ')[0]}`}>
                            {selectedInterview.ai_analysis.overall_score}%
                          </div>
                          <p className="text-sm text-muted-foreground mt-2">Overall Score</p>
                        </div>
                      ) : (
                        <p className="text-muted-foreground">No AI assessment yet</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Integrity Score */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Integrity Score
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex items-center justify-center">
                      {getProctoringMetrics() ? (
                        <IntegrityScoreRing score={getProctoringMetrics().integrityScore} />
                      ) : (
                        <p className="text-muted-foreground">No proctoring data</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Recordings */}
                {selectedInterview.full_recording_url && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Video className="w-4 h-4" />
                        Full Interview Recording
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-2">
                        <Button onClick={() => openVideoModal(selectedInterview.full_recording_url, 'Full Interview Recording')}>
                          <Play className="w-4 h-4 mr-2" />
                          Watch Recording
                        </Button>
                        <Button variant="outline" onClick={() => downloadAllVideos(selectedInterview.id, selectedInterview.student_name)}>
                          <Download className="w-4 h-4 mr-2" />
                          Download All Videos
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Consent Recording */}
                {selectedInterview.consent_recording && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Consent Recording
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Button variant="outline" onClick={() => openVideoModal(selectedInterview.consent_recording, 'Consent Recording')}>
                        <Play className="w-4 h-4 mr-2" />
                        Play Consent Recording
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Answers Tab */}
              <TabsContent value="answers" className="space-y-4 mt-4">
                {selectedInterview.questions_answers?.length > 0 ? (
                  selectedInterview.questions_answers.map((qa, idx) => (
                    <Card key={idx}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-sm font-medium">
                            Question {idx + 1}
                          </CardTitle>
                          {qa.score !== undefined && (
                            <Badge className={getScoreColor(qa.score)}>
                              Score: {Math.round(qa.score)}%
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-foreground mt-2 font-normal">
                          {qa.question_text || qa.question || 'Question not available'}
                        </p>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1 font-medium">Student's Answer (Transcript)</p>
                          <p className="text-sm bg-muted/50 p-3 rounded-lg">
                            {qa.transcript || 'No transcript available'}
                          </p>
                        </div>
                        {qa.keywords_matched && qa.keywords_matched.length > 0 && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Keywords Matched</p>
                            <div className="flex flex-wrap gap-1">
                              {qa.keywords_matched.map((kw, i) => (
                                <Badge key={i} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                  {kw}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {qa.ai_feedback && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">AI Feedback</p>
                            <p className="text-sm text-muted-foreground italic">
                              {qa.ai_feedback}
                            </p>
                          </div>
                        )}
                        {qa.video_url && (
                          <Button variant="outline" size="sm" onClick={() => openVideoModal(qa.video_url, `Question ${idx + 1} Response`)}>
                            <Play className="w-3 h-3 mr-1" />
                            Watch Response
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    <p>No question responses recorded yet</p>
                  </div>
                )}
              </TabsContent>

              {/* Proctoring Tab */}
              <TabsContent value="proctoring" className="space-y-4 mt-4">
                {getProctoringMetrics() ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <ProctoringCard
                      icon={EyeIcon}
                      title="Eye Contact"
                      value={Math.round(getProctoringMetrics().eyeContactPercent)}
                      unit="%"
                      status={getProctoringMetrics().eyeContactPercent >= 60 ? 'good' : getProctoringMetrics().eyeContactPercent >= 40 ? 'warning' : 'critical'}
                      description="Time spent looking at camera"
                    />
                    <ProctoringCard
                      icon={MonitorX}
                      title="Tab Switches"
                      value={getProctoringMetrics().tabSwitches}
                      status={getProctoringMetrics().tabSwitches === 0 ? 'good' : getProctoringMetrics().tabSwitches <= 2 ? 'warning' : 'critical'}
                      description="Times candidate switched tabs"
                    />
                    <ProctoringCard
                      icon={ScanFace}
                      title="Multiple Faces"
                      value={getProctoringMetrics().facesDetected}
                      status={getProctoringMetrics().facesDetected === 0 ? 'good' : 'critical'}
                      description="Instances of multiple faces detected"
                    />
                    <ProctoringCard
                      icon={AlertTriangle}
                      title="Face Not Visible"
                      value={getProctoringMetrics().noFaceCount}
                      status={getProctoringMetrics().noFaceCount <= 3 ? 'good' : getProctoringMetrics().noFaceCount <= 10 ? 'warning' : 'critical'}
                      description="Times face was not detected"
                    />
                    <ProctoringCard
                      icon={Target}
                      title="Detection Confidence"
                      value={Math.round(getProctoringMetrics().avgConfidence * 100)}
                      unit="%"
                      status={getProctoringMetrics().avgConfidence >= 0.8 ? 'good' : getProctoringMetrics().avgConfidence >= 0.6 ? 'warning' : 'critical'}
                      description="Average face detection confidence"
                    />
                    <ProctoringCard
                      icon={Shield}
                      title="Integrity Score"
                      value={getProctoringMetrics().integrityScore}
                      unit="%"
                      status={getProctoringMetrics().integrityScore >= 80 ? 'good' : getProctoringMetrics().integrityScore >= 60 ? 'warning' : 'critical'}
                      description="Overall interview integrity"
                    />
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Shield className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    <p>No proctoring data available</p>
                  </div>
                )}
              </TabsContent>

              {/* AI Analysis Tab */}
              <TabsContent value="ai" className="space-y-4 mt-4">
                {selectedInterview.ai_analysis ? (
                  <div className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-yellow-500" />
                          AI Assessment Summary
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="text-center p-4 bg-muted/50 rounded-lg">
                            <p className="text-3xl font-bold text-lime-500">{selectedInterview.ai_analysis.overall_score}%</p>
                            <p className="text-sm text-muted-foreground">Overall Score</p>
                          </div>
                          {selectedInterview.ai_analysis.communication_score !== undefined && (
                            <div className="text-center p-4 bg-muted/50 rounded-lg">
                              <p className="text-3xl font-bold text-blue-500">{selectedInterview.ai_analysis.communication_score}%</p>
                              <p className="text-sm text-muted-foreground">Communication</p>
                            </div>
                          )}
                          {selectedInterview.ai_analysis.knowledge_score !== undefined && (
                            <div className="text-center p-4 bg-muted/50 rounded-lg">
                              <p className="text-3xl font-bold text-purple-500">{selectedInterview.ai_analysis.knowledge_score}%</p>
                              <p className="text-sm text-muted-foreground">Knowledge</p>
                            </div>
                          )}
                          {selectedInterview.ai_analysis.confidence_score !== undefined && (
                            <div className="text-center p-4 bg-muted/50 rounded-lg">
                              <p className="text-3xl font-bold text-orange-500">{selectedInterview.ai_analysis.confidence_score}%</p>
                              <p className="text-sm text-muted-foreground">Confidence</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Show Final Verdict prominently when admin has reviewed */}
                        {selectedInterview.admin_reviewed && selectedInterview.final_verdict && (
                          <div className={`p-4 rounded-lg border-2 ${
                            selectedInterview.final_verdict === 'pass' 
                              ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                              : 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          }`}>
                            <p className="text-sm text-muted-foreground mb-2">Final Verdict (Admin Reviewed)</p>
                            <div className="flex items-center gap-2">
                              {selectedInterview.final_verdict === 'pass' ? (
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                              ) : (
                                <XCircle className="w-5 h-5 text-red-600" />
                              )}
                              <p className={`font-bold text-lg ${
                                selectedInterview.final_verdict === 'pass' ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {selectedInterview.final_verdict.toUpperCase()}
                              </p>
                            </div>
                            {selectedInterview.final_verdict === 'pass' && selectedInterview.ai_analysis?.verdict?.toLowerCase().includes('fail') && (
                              <p className="text-sm text-green-700 dark:text-green-400 mt-2">
                                Note: AI flagged concerns but after manual review, this interview was approved.
                              </p>
                            )}
                          </div>
                        )}

                        {/* Show AI Verdict (only if no admin override OR admin confirmed the same verdict) */}
                        {selectedInterview.ai_analysis.verdict && (
                          <div className="p-4 rounded-lg border">
                            <p className="text-sm text-muted-foreground mb-2">AI Verdict (Initial Assessment)</p>
                            <p className="font-medium">{selectedInterview.ai_analysis.verdict}</p>
                            {selectedInterview.admin_reviewed && selectedInterview.final_verdict === 'pass' && 
                             selectedInterview.ai_analysis.verdict?.toLowerCase().includes('fail') && (
                              <p className="text-xs text-muted-foreground mt-1 italic">
                                This was the AI's initial assessment before admin review.
                              </p>
                            )}
                          </div>
                        )}
                        
                        {selectedInterview.ai_analysis.summary && (
                          <div className="p-4 rounded-lg border">
                            <p className="text-sm text-muted-foreground mb-2">Summary</p>
                            <p className="text-sm">{selectedInterview.ai_analysis.summary}</p>
                          </div>
                        )}
                        
                        {selectedInterview.ai_analysis.strengths?.length > 0 && (
                          <div className="p-4 rounded-lg border border-green-500/30 bg-green-500/10">
                            <p className="text-sm text-green-600 dark:text-green-400 mb-2 font-medium">Strengths</p>
                            <ul className="list-disc list-inside space-y-1">
                              {selectedInterview.ai_analysis.strengths.map((s, i) => (
                                <li key={i} className="text-sm">{s}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {selectedInterview.ai_analysis.weaknesses?.length > 0 && (
                          <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/10">
                            <p className="text-sm text-red-600 dark:text-red-400 mb-2 font-medium">Areas for Improvement</p>
                            <ul className="list-disc list-inside space-y-1">
                              {selectedInterview.ai_analysis.weaknesses.map((w, i) => (
                                <li key={i} className="text-sm">{w}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Brain className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    <p>No AI analysis available yet</p>
                    <p className="text-sm mt-1">AI analysis is generated after interview completion</p>
                  </div>
                )}
              </TabsContent>
              </>
                );
              })()}
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
